import { useState } from 'react';
import { Trans } from '@lingui/react/macro';

import { PriceAggregate, PriceAmountWithDetails } from '@/api';
import { DisplayPrice } from '@/components/PricesList/DisplayPrice';
import { EditPrice } from '@/components/PricesList/EditPrice';
import { PricesContainer } from '@/components/PricesList/PricesContainer';
import { PricesLine } from '@/components/PricesList/PricesLine';
import WorkshopPriceLabel from '@/components/WorkshopPriceLabel/WorkshopPriceLabel';
import { Label } from '@/design_system/Label/Label';
import Stack from '@/design_system/Stack';
import { arePricesEqual } from '@/models/actionType';
import { TEMPORARY_ID_PREFIX, useUpdateAction } from '@/models/article';
import { type ArticleActionWithRelations, type ArticleWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import useViewPort from '@/utils/useViewport';

export const ActionPrices = ({
  action,
  previousAction,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
}) => {
  const { view: requestView } = useRequestContext();
  const { view } = useArticleContext();
  const { isMobile } = useViewPort();

  if (!requestView.workshopPrice.shown && !requestView.organizationPrice.shown) {
    return null;
  }

  const hasSpareParts = !!(action ?? previousAction)?.spareParts?.length;
  const canSeeSparePartPrices = view.services.actions.fields.spareParts.prices.shown;
  const showSparePartsAndLaborLines = hasSpareParts && canSeeSparePartPrices;

  const noVATAppliedOnWorkshopPrice = action?.workshopPrice?.amountPerEntity?.some(
    (a) => a.noVATApplied
  );

  if (isMobile) {
    return (
      <Stack
        gap=".5rem"
        padding="12px"
        style={{
          backgroundColor: 'var(--color-neutral-100)',
        }}
      >
        {requestView.workshopPrice.shown && (
          <PriceSection
            type="workshop"
            label={requestView.workshopPrice.label}
            action={action}
            previousAction={previousAction}
            showSparePartsAndLaborLines={showSparePartsAndLaborLines}
          />
        )}

        {requestView.organizationPrice.shown && (
          <PriceSection
            type="organization"
            label={requestView.organizationPrice.label}
            action={action}
            previousAction={previousAction}
            showSparePartsAndLaborLines={showSparePartsAndLaborLines}
          />
        )}
      </Stack>
    );
  }

  return (
    <PricesContainer isDisabled={!action} noVATAppliedOnWorkshopPrice={noVATAppliedOnWorkshopPrice}>
      {showSparePartsAndLaborLines && (
        <>
          <SparePartsPricesLine action={action} previousAction={previousAction} />
          <LaborPricesLine action={action} previousAction={previousAction} />
        </>
      )}
      <TotalPricesLine action={action} previousAction={previousAction} />
    </PricesContainer>
  );
};

const SparePartsPricesLine = ({
  action,
  previousAction,
  type,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
  type?: 'organization' | 'workshop' | undefined;
}) => {
  return (
    <PricesLine
      label={<Trans id="article.form.actions.price.spare-parts">Spare parts</Trans>}
      fontWeight="regular"
      type={type}
      disabled={!action}
      workshopPrice={
        <Price
          action={action}
          previousAction={previousAction}
          type="workshop"
          line="spareParts"
          fontWeight="regular"
        />
      }
      organizationPrice={
        <Price
          action={action}
          previousAction={previousAction}
          type="organization"
          line="spareParts"
          fontWeight="regular"
        />
      }
    />
  );
};

const LaborPricesLine = ({
  action,
  previousAction,
  type,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
  type?: 'organization' | 'workshop';
}) => {
  return (
    <PricesLine
      label={<Trans id="article.form.actions.price.labor">Labor</Trans>}
      fontWeight="regular"
      type={type}
      disabled={!action}
      workshopPrice={
        <Price
          action={action}
          previousAction={previousAction}
          type="workshop"
          line="labor"
          fontWeight="regular"
        />
      }
      organizationPrice={
        <Price
          action={action}
          previousAction={previousAction}
          type="organization"
          line="labor"
          fontWeight="regular"
        />
      }
    />
  );
};

const TotalPricesLine = ({
  action,
  previousAction,
  type,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
  type?: 'organization' | 'workshop';
}) => {
  const { view: requestView } = useRequestContext();

  const showTotalLabel = requestView.workshopPrice.shown && requestView.organizationPrice.shown;
  const hasSpareParts = !!(action ?? previousAction)?.spareParts?.length;

  const noVATApplied = action?.workshopPrice?.amountPerEntity?.some(
    (amount) => amount.noVATApplied
  );

  return (
    <PricesLine
      label={
        showTotalLabel ? (
          <Trans id="article.form.actions.price.total">Total</Trans>
        ) : requestView.workshopPrice.shown ? (
          <WorkshopPriceLabel noVATApplied={noVATApplied} />
        ) : (
          requestView.organizationPrice.label
        )
      }
      fontWeight="medium"
      type={type}
      disabled={!action}
      workshopPrice={
        <Price
          action={action}
          previousAction={previousAction}
          type="workshop"
          line={hasSpareParts ? 'total' : 'labor'}
          fontWeight="medium"
        />
      }
      organizationPrice={
        <Price
          action={action}
          previousAction={previousAction}
          type="organization"
          line={hasSpareParts ? 'total' : 'labor'}
          fontWeight="medium"
        />
      }
    />
  );
};

const Price = ({
  action,
  previousAction,
  line,
  type,
  fontWeight,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
  type: 'workshop' | 'organization';
  line: 'spareParts' | 'labor' | 'total';
  fontWeight: 'regular' | 'medium';
}) => {
  const { view, article, errors, state } = useArticleContext();
  const { isMobile } = useViewPort();

  const actionOrPreviousAction = (action ?? previousAction)!;

  const actionPrices = getActionPrices({
    action,
    previousAction,
    type,
    article,
  });

  const price = actionPrices[line].price;
  const isEdited = actionPrices[line].isEdited;

  const isInvalid =
    line === 'labor' &&
    (type === 'workshop'
      ? errors.services?.actions?.actions?.find((act) => act.id === actionOrPreviousAction.id)
          ?.missingWorkshopPrice
      : errors.services?.actions?.actions?.find((act) => act.id === actionOrPreviousAction.id)
          ?.missingOrganizationPrice);

  const isLaborPriceEditable =
    !!action &&
    !action.id.startsWith(TEMPORARY_ID_PREFIX) &&
    !isMobile &&
    (type === 'workshop'
      ? view.services.actions.fields.workshopPrice.editable
      : view.services.actions.fields.organizationPrice.editable);

  const isEditable = line === 'labor' && isLaborPriceEditable;

  const isEditing = !!state.priceUpdates.updatesInProgress.find(
    (priceUpdate) =>
      priceUpdate.actionId === action?.id && priceUpdate.type === type && !priceUpdate.sparePartId
  );

  const isDeletedAction = !action;

  if (isEditable && isEditing) {
    return (
      <EditLaborPrice
        action={action}
        type={type}
        closeEditionMode={() =>
          state.priceUpdates.setUpdatesInProgress((prevState) =>
            prevState.filter(
              (priceUpdate) => !(priceUpdate.actionId === action.id && priceUpdate.type === type)
            )
          )
        }
      />
    );
  }

  const sparePartPricesForBase =
    line === 'spareParts'
      ? actionOrPreviousAction.spareParts.map((sparePart) => ({
          sparePart,
          price:
            type === 'workshop'
              ? sparePart.workshopPrice?.amountPerEntity?.find(
                  (amount) => amount.entityId === article.workshopId
                )
              : sparePart.organizationPrice?.amountPerEntity?.[0],
        }))
      : undefined;

  return (
    <DisplayPrice
      line={line}
      type={type}
      fontWeight={fontWeight}
      price={price}
      isEdited={isEdited}
      isInvalid={isInvalid}
      isEditable={isEditable}
      isDisabled={isDeletedAction}
      customOrganizationPrice={actionOrPreviousAction.customOrganizationPrice}
      sparePartPricesForBase={sparePartPricesForBase}
      isAnotherLineEditable={isLaborPriceEditable && line !== 'labor'}
      onPressEdit={() =>
        state.priceUpdates.setUpdatesInProgress((prevState) => [
          ...prevState,
          { actionId: action!.id, type },
        ])
      }
    />
  );
};

const EditLaborPrice = ({
  action,
  type,
  closeEditionMode,
}: {
  action: ArticleActionWithRelations;
  type: 'workshop' | 'organization';
  closeEditionMode: () => void;
}) => {
  const { errors } = useArticleContext();

  const { value, setValue, currency, savePrice, label, isLoading } = usePriceEditionState({
    action,
    type,
  });

  const hasError =
    type === 'workshop'
      ? errors.services?.actions?.actions?.find(({ id }) => action.id === id)
          ?.workshopPriceUpdateInProgress
      : errors.services?.actions?.actions?.find(({ id }) => action.id === id)
          ?.organizationPriceUpdateInProgress;

  if (!currency) {
    return null;
  }

  return (
    <EditPrice
      closeEditionMode={closeEditionMode}
      label={label}
      currency={currency}
      value={value}
      setValue={setValue}
      savePrice={savePrice}
      isLoading={isLoading}
      hasError={hasError}
    />
  );
};

const PriceSection = ({
  type,
  label,
  action,
  previousAction,
  showSparePartsAndLaborLines,
}: {
  type: 'workshop' | 'organization';
  label: string;
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
  showSparePartsAndLaborLines?: boolean;
}) => {
  const stackProps = {
    row: true,
    justifyContent: 'space-between',
    className: 'paragraph-100-medium', // TODO: remove this by checking each children text in details
  };

  if (showSparePartsAndLaborLines) {
    return (
      <>
        <Label label={label} />
        <hr />

        <Stack {...stackProps}>
          <SparePartsPricesLine action={action} previousAction={previousAction} type={type} />
        </Stack>

        <Stack {...stackProps}>
          <LaborPricesLine action={action} previousAction={previousAction} type={type} />
        </Stack>

        <Stack {...stackProps} style={{ paddingBottom: '.5rem' }}>
          <TotalPricesLine action={action} previousAction={previousAction} type={type} />
        </Stack>
      </>
    );
  }

  return (
    <Stack row justifyContent="space-between">
      <Label label={label} />
      <Price
        action={action}
        previousAction={previousAction}
        type={type}
        line={'total'}
        fontWeight="medium"
      />
    </Stack>
  );
};

export const usePriceEditionState = ({
  action,
  type,
}: {
  action: ArticleActionWithRelations;
  type: 'workshop' | 'organization';
}) => {
  const { view: requestView } = useRequestContext();
  const { request, article, view } = useArticleContext();

  const showAmountBeforeTaxes =
    type === 'workshop'
      ? requestView.workshopPrice.showAmountBeforeTaxes
      : requestView.organizationPrice.showAmountBeforeTaxes;

  const customFieldName = type === 'workshop' ? 'customWorkshopPrice' : 'customOrganizationPrice';
  const catalogFieldName =
    type === 'workshop' ? 'catalogWorkshopPrice' : 'catalogOrganizationPrice';

  const customPrice = action[customFieldName];
  const catalogPrice = action[catalogFieldName];

  const currency = customPrice?.currency ?? catalogPrice?.currency;

  const initialValue = showAmountBeforeTaxes
    ? (customPrice?.amountBeforeTaxes ?? catalogPrice?.amountBeforeTaxes)
    : (customPrice?.amount ?? catalogPrice?.amount);

  const [value, setValue] = useState(initialValue ?? undefined);

  const { mutateAsync: updateAction, isPending: isUpdateActionPending } = useUpdateAction({
    articleId: article.id,
    requestId: request.id,
  });

  const label =
    type === 'workshop' ? requestView.workshopPrice.label : requestView.organizationPrice.label;
  const isRequired =
    type === 'workshop'
      ? view.services.actions.fields.workshopPrice.required
      : view.services.actions.fields.organizationPrice.required;

  const savePrice = async (newValue: number | undefined) => {
    await updateAction({
      actionId: action.id,
      body: {
        [customFieldName]:
          newValue !== undefined && !isNaN(newValue)
            ? showAmountBeforeTaxes
              ? { amountBeforeTaxes: newValue }
              : { amount: newValue }
            : null,
      },
    });
  };

  return {
    value,
    setValue,
    currency,
    savePrice,
    initialValue: initialValue ?? undefined,
    label,
    isRequired,
    isLoading: isUpdateActionPending,
  };
};

const getActionPrices = ({
  action,
  previousAction,
  type,
  article,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
  type: 'workshop' | 'organization';
  article: ArticleWithRelations;
}): {
  total: {
    price: PriceAmountWithDetails | undefined;
    isEdited: boolean;
  };
  labor: {
    price: PriceAmountWithDetails | undefined;
    isEdited: boolean;
  };
  spareParts: {
    price: PriceAmountWithDetails | undefined;
    isEdited: boolean;
  };
} => {
  const getPrices = ({ action }: { action?: ArticleActionWithRelations }) => {
    const totalPriceAggregate =
      type === 'workshop' ? action?.workshopPrice : action?.organizationPrice;

    const laborPriceAggregate = totalPriceAggregate?.components?.find(
      (component) => component.subType === 'action-labor'
    );
    const sparePartsPriceAggregate = totalPriceAggregate?.components?.find(
      (component) => component.subType === 'spare-parts'
    );

    const getPrice = (
      priceAggregate: PriceAggregate | undefined
    ): PriceAmountWithDetails | undefined => {
      return type === 'workshop'
        ? priceAggregate?.amountPerEntity?.find((amount) => amount.entityId === article.workshopId)
        : priceAggregate?.amountPerEntity?.[0];
    };

    const totalPrice = getPrice(totalPriceAggregate);
    const laborPrice = getPrice(laborPriceAggregate);
    const sparePartsPrice = getPrice(sparePartsPriceAggregate);

    return {
      totalPrice,
      laborPrice,
      sparePartsPrice,
    };
  };

  const { totalPrice, laborPrice, sparePartsPrice } = getPrices({ action });

  const {
    totalPrice: previousTotalPrice,
    laborPrice: previousLaborPrice,
    sparePartsPrice: previousSparePartsPrice,
  } = getPrices({ action: previousAction });

  const isTotalPriceEdited =
    !!action && !!previousAction && !arePricesEqual(totalPrice, previousTotalPrice);
  const isLaborPriceEdited =
    !!action && !!previousAction && !arePricesEqual(laborPrice, previousLaborPrice);
  const isSparePartsPriceEdited =
    !!action && !!previousAction && !arePricesEqual(sparePartsPrice, previousSparePartsPrice);

  return {
    total: {
      price: action ? totalPrice : previousTotalPrice,
      isEdited: isTotalPriceEdited,
    },
    labor: {
      price: action ? laborPrice : previousLaborPrice,
      isEdited: isLaborPriceEdited,
    },
    spareParts: {
      price: action ? sparePartsPrice : previousSparePartsPrice,
      isEdited: isSparePartsPriceEdited,
    },
  };
};
